import { useEffect, useRef, useState } from "react";
import { observer } from "mobx-react-lite";
import { appState } from "@/app/appState";
import { documentFileUrl } from "@/apiExtra/documentFiles";
import { containNestedScroll } from "@/components/scrollUtils";

type Props = {
    documentId: number;
    filename?: string | null;
    fill?: boolean;
};

export const TextViewer = observer(({ documentId, filename, fill }: Props) => {
    const scrollRef = useRef<HTMLDivElement>(null);
    const [error, setError] = useState<string | null>(null);
    const [loading, setLoading] = useState(true);
    const [text, setText] = useState("");

    useEffect(() => {
        const el = scrollRef.current;
        if (!el) return;
        return containNestedScroll(el);
    }, [documentId, fill]);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        setError(null);
        setText("");

        (async () => {
            try {
                const res = await fetch(documentFileUrl(documentId), { credentials: "include" });
                if (!res.ok) throw new Error(appState.t("chat.failedToLoad"));
                const body = await res.text();
                if (cancelled) return;
                setText(body);
            } catch (e) {
                if (cancelled) return;
                setError(e instanceof Error ? e.message : appState.t("chat.failedToLoad"));
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [documentId]);

    return (
        <div
            ref={scrollRef}
            className={`doc-viewer ${fill ? "doc-viewer-fill" : ""}`}
            title={filename || undefined}
        >
            {loading && (
                <div className="doc-status">{appState.t("common.loading")}</div>
            )}
            {error && (
                <div className="doc-status">{error}</div>
            )}
            {!loading && !error && (
                <pre className="doc-text">
                    {text}
                </pre>
            )}
        </div>
    );
});
